/* Guided product tour — overlay + spotlight + positioned popover.
 *
 * Walks a brand-new user through Dashboard → AI Coach → Inbox → Pipeline →
 * Recordings → Buyers → Settings in ~8 stops. Auto-navigates between
 * routes between stops. Persists completion in localStorage so it never
 * re-shows for a user who finished or skipped.
 *
 * Trigger sources:
 *  • Auto-launches once after a successful demo seed (if not previously completed)
 *  • Manual launch from the DemoBanner's "Take the tour" button (always works)
 *
 * Implementation notes:
 *  • No external library — keeps bundle small + zero dependency churn
 *  • Target highlighting: clones the target element's bounding rect into a
 *    fixed-position SVG mask so the rest of the screen dims while the
 *    target shows through crisply with a pulsing outline
 *  • Popover positioning: 4 placements (top/bottom/left/right) auto-flip
 *    if the chosen side overflows the viewport
 */
import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, Sparkles, CheckCircle2 } from "lucide-react";

const STORAGE_KEY = "isc_tour_completed_v1";

const STEPS = [
  {
    id: "welcome",
    route: "/v3",
    target: null, // centered modal — no target highlight
    title: "Welcome to iSpeedToClose 👋",
    body: (
      <>
        Quick 60-second tour of the wholesale workflow. We'll show you the AI Coach,
        Inbox, Pipeline, Call Recordings, and where to connect Twilio and Stripe.
        <br /><br />
        You can skip anytime — clear demo data later from the yellow banner.
      </>
    ),
  },
  {
    id: "coach",
    route: "/v3",
    target: '[data-testid="coach-widget"], [data-testid="coach-widget-empty"]',
    placement: "bottom",
    title: "🏆 AI Call Coach",
    body: "Every recorded call is auto-scored 0-100 by Claude. Best calls and coachable moments surface right here so you improve every week.",
  },
  {
    id: "inbox",
    route: "/v3/crm/inbox",
    target: null,
    title: "📥 Inbox = AI-vetted deals",
    body: "Every lead gets a Claude verdict: GO, MAYBE, or NO-GO. The Inbox shows only GO deals by default — your daily 'work this' list.",
  },
  {
    id: "pipeline",
    route: "/v3/crm/pipeline",
    target: null,
    title: "📊 Drag-drop Pipeline",
    body: "Move leads through stages by dragging. Each card has a green Rec call button to dial-and-record in one click, plus Send for Signature for DocuSign.",
  },
  {
    id: "recordings",
    route: "/v3/crm/outreach/recordings",
    target: null,
    title: "🎙️ Call Recordings",
    body: "Every Twilio call auto-transcribes (Whisper) and gets analyzed (Claude). Click any recording to see the AI summary, transcript, and coaching feedback.",
  },
  {
    id: "buyers",
    route: "/v3/crm/buyers",
    target: null,
    title: "💰 Cash Buyer CRM",
    body: "Unified buyer database with hot-list scoring + bulk SMS blast — find the right buyer for every deal in seconds.",
  },
  {
    id: "settings",
    route: "/v3/settings",
    target: null,
    title: "⚙️ Connect your accounts",
    body: "Bring your own Twilio (SMS + Voice), Stripe (billing), Resend (email), DocuSign (contracts). Multi-tenant means YOUR Twilio account = YOUR phone bill, you control everything.",
  },
  {
    id: "done",
    route: "/v3",
    target: null,
    title: "🚀 You're set!",
    body: (
      <>
        Demo data is loaded so you can click around freely. When you're ready, clear
        the demo banner and start adding real leads.
        <br /><br />
        Make your first <strong>recorded call</strong> from any Lead detail page and
        watch the AI Coach score appear ~60 seconds later.
      </>
    ),
  },
];


// ─── Context
const TourCtx = createContext(null);

export function useTour() {
  return useContext(TourCtx);
}

export function TourProvider({ children }) {
  const [active, setActive] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);

  const start = useCallback(() => {
    setStepIdx(0);
    setActive(true);
  }, []);

  const close = useCallback((completed = false) => {
    setActive(false);
    setStepIdx(0);
    if (completed) {
      try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
    }
  }, []);

  const next = useCallback(() => {
    setStepIdx((i) => {
      if (i >= STEPS.length - 1) {
        try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
        setActive(false);
        return 0;
      }
      return i + 1;
    });
  }, []);

  const prev = useCallback(() => {
    setStepIdx((i) => Math.max(0, i - 1));
  }, []);

  // Public flag the DemoBanner uses to decide whether the "Take the tour"
  // button should still be highlighted.
  const isCompleted = (() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; }
    catch { return false; }
  })();

  return (
    <TourCtx.Provider value={{ active, stepIdx, start, close, next, prev, isCompleted, total: STEPS.length }}>
      {children}
      {active && <TourOverlay step={STEPS[stepIdx]} stepIdx={stepIdx} total={STEPS.length}
                               onNext={next} onPrev={prev} onClose={() => close(false)} />}
    </TourCtx.Provider>
  );
}


// ─── Overlay
function TourOverlay({ step, stepIdx, total, onNext, onPrev, onClose }) {
  const nav = useNavigate();
  const loc = useLocation();
  const [targetRect, setTargetRect] = useState(null);
  const [popoverPos, setPopoverPos] = useState(null);
  const popoverRef = useRef(null);

  // 1. Navigate to the step's route if we're not already there.
  useEffect(() => {
    if (step.route && loc.pathname !== step.route) {
      nav(step.route);
    }
  }, [step.route]);

  // 2. Find the target element + position the popover.
  useEffect(() => {
    if (!step.target) {
      setTargetRect(null);
      setPopoverPos({ centered: true });
      return undefined;
    }
    // Poll for the target — page may still be loading.
    let cancelled = false;
    const tryFind = () => {
      if (cancelled) return;
      const el = document.querySelector(step.target);
      if (!el) {
        setTimeout(tryFind, 200);
        return;
      }
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      // Let the smooth-scroll settle before snapshotting position.
      setTimeout(() => {
        if (cancelled) return;
        const rect = el.getBoundingClientRect();
        setTargetRect({
          top: rect.top, left: rect.left,
          width: rect.width, height: rect.height,
        });
      }, 400);
    };
    tryFind();
    return () => { cancelled = true; };
  }, [step.target, step.id]);

  // 3. Auto-position popover relative to target (with auto-flip).
  useEffect(() => {
    if (!targetRect || !popoverRef.current) return;
    const pop = popoverRef.current.getBoundingClientRect();
    const margin = 14;
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const placement = step.placement || "bottom";

    let top = 0, left = 0;
    const tryPlace = (p) => {
      switch (p) {
        case "top":
          top = targetRect.top - pop.height - margin;
          left = targetRect.left + targetRect.width / 2 - pop.width / 2;
          break;
        case "bottom":
          top = targetRect.top + targetRect.height + margin;
          left = targetRect.left + targetRect.width / 2 - pop.width / 2;
          break;
        case "left":
          top = targetRect.top + targetRect.height / 2 - pop.height / 2;
          left = targetRect.left - pop.width - margin;
          break;
        default: // right
          top = targetRect.top + targetRect.height / 2 - pop.height / 2;
          left = targetRect.left + targetRect.width + margin;
      }
    };
    tryPlace(placement);
    // Flip if overflowing.
    if (top < 8) tryPlace(placement === "top" ? "bottom" : placement);
    if (top + pop.height > vh - 8) tryPlace(placement === "bottom" ? "top" : placement);
    if (left < 8) left = 8;
    if (left + pop.width > vw - 8) left = vw - pop.width - 8;
    setPopoverPos({ top, left, centered: false });
  }, [targetRect, step.placement]);

  // 4. Keyboard nav.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") onNext();
      else if (e.key === "ArrowLeft") onPrev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onNext, onPrev]);

  if (!popoverPos) return null;

  const isLast = stepIdx === total - 1;
  const isFirst = stepIdx === 0;

  return (
    <div className="fixed inset-0 z-[100]" data-testid="tour-overlay">
      {/* Backdrop with optional spotlight cutout */}
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: "auto" }} onClick={onClose}>
        <defs>
          <mask id="tour-mask">
            <rect width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={targetRect.left - 6}
                y={targetRect.top - 6}
                width={targetRect.width + 12}
                height={targetRect.height + 12}
                rx="10"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(15, 23, 42, 0.65)"
          mask="url(#tour-mask)"
        />
      </svg>

      {/* Pulsing outline around target */}
      {targetRect && (
        <div
          className="absolute pointer-events-none rounded-[10px]"
          style={{
            top: targetRect.top - 6,
            left: targetRect.left - 6,
            width: targetRect.width + 12,
            height: targetRect.height + 12,
            boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.85), 0 0 24px 8px rgba(99, 102, 241, 0.45)",
            animation: "tour-pulse 1.6s ease-in-out infinite",
          }}
        />
      )}

      {/* Popover */}
      <div
        ref={popoverRef}
        className="absolute bg-white rounded-xl shadow-2xl border border-indigo-200 p-5 max-w-md"
        style={popoverPos.centered
          ? { top: "50%", left: "50%", transform: "translate(-50%, -50%)" }
          : { top: popoverPos.top, left: popoverPos.left }}
        onClick={(e) => e.stopPropagation()}
        data-testid={`tour-step-${step.id}`}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-2.5 right-2.5 w-7 h-7 rounded-md hover:bg-slate-100 text-slate-500 flex items-center justify-center transition"
          title="Skip tour"
          data-testid="tour-skip"
        >
          <X size={14} />
        </button>

        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={14} className="text-indigo-600" />
          <span className="text-[10px] uppercase tracking-wider font-bold text-indigo-700">
            Step {stepIdx + 1} of {total}
          </span>
        </div>

        <h3 className="font-display font-extrabold text-lg leading-tight mb-2">
          {step.title}
        </h3>

        <div className="text-sm text-[var(--ink-2)] leading-relaxed mb-4">
          {step.body}
        </div>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 mb-4">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={"h-1.5 rounded-full transition-all " + (
                i === stepIdx ? "w-6 bg-indigo-600" :
                i < stepIdx  ? "w-1.5 bg-indigo-300" :
                              "w-1.5 bg-slate-200"
              )}
            />
          ))}
        </div>

        {/* Footer buttons */}
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-[var(--ink-3)] hover:text-[var(--ink)] font-semibold transition"
            data-testid="tour-skip-btn"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            {!isFirst && (
              <button
                type="button"
                onClick={onPrev}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 text-sm font-semibold transition"
                data-testid="tour-prev"
              >
                <ChevronLeft size={13} /> Back
              </button>
            )}
            <button
              type="button"
              onClick={onNext}
              className="inline-flex items-center gap-1 px-4 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold transition"
              data-testid="tour-next"
            >
              {isLast ? <><CheckCircle2 size={13} /> Finish</> : <>Next <ChevronRight size={13} /></>}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes tour-pulse {
          0%, 100% { box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.85), 0 0 24px 8px rgba(99, 102, 241, 0.45); }
          50%      { box-shadow: 0 0 0 6px rgba(99, 102, 241, 0.95), 0 0 32px 12px rgba(99, 102, 241, 0.6); }
        }
      `}</style>
    </div>
  );
}
